import * as THREE from 'three'
import { FIRST_PERSON_LOOK_SPEED } from '@/shared/constants'
import type {
  CapsuleCharacterMotorQueries,
  CharacterCommand,
  CharacterCreateOptions,
  CharacterFirstPersonViewOptions,
  CharacterHandle,
  CharacterSnapshot,
  CharacterStablePlacementOptions,
  CharacterStablePlacementResult,
  CharacterThirdPersonViewBindingOptions,
  CharacterThirdPersonViewOptions,
  CharacterViewBinding,
  CharacterViewBindingOptions,
} from '@/shared/types/character'
import type {
  CharacterService as CharacterServiceContract,
  SpatialQueryService,
} from '@/shared/types/services'
import type { SpatialQueryBindingOptions } from '@/shared/types/spatial'
import {
  CapsuleCharacterMotor,
  type CapsuleCharacterMotorSnapshot,
} from './motor/CapsuleCharacterMotor'

interface CharacterRecord {
  id: string
  handle: CharacterHandle
  motor: CapsuleCharacterMotor
  command: CharacterCommand
  snapshot: CharacterSnapshot
  spatialQuery: SpatialQueryBindingOptions
}

const DEFAULT_FIRST_PERSON_VIEW_OPTIONS: CharacterFirstPersonViewOptions = {
  lookInteraction: 'drag-look',
  lookSensitivity: FIRST_PERSON_LOOK_SPEED,
  dragButton: 0,
  pointerLockButton: 0,
  minPitch: -Math.PI / 2 + 1e-4,
  maxPitch: Math.PI / 2 - 1e-4,
}

const DEFAULT_THIRD_PERSON_VIEW_OPTIONS: CharacterThirdPersonViewOptions = {
  lookSensitivity: FIRST_PERSON_LOOK_SPEED * 1.5,
  dragButton: 0,
  followDistance: 4.5,
  minDistance: 1.5,
  maxDistance: 12,
  zoomSensitivity: 0.01,
  minPitch: -Math.PI / 3,
  maxPitch: Math.PI / 2 - 1e-4,
  cameraCollision: {
    enabled: false,
    radius: 0.22,
    skinWidth: 0.08,
    minDistance: 0.75,
    pullInSmoothing: 20,
    restoreSmoothing: 8,
  },
}

const DEFAULT_COMMAND: CharacterCommand = {
  move: {
    forward: 0,
    right: 0,
  },
  run: false,
  jump: false,
  bodyYaw: 0,
  aimYaw: 0,
  aimPitch: 0,
}

/** 角色服务实现。 */
export class CharacterServiceImpl implements CharacterServiceContract {
  private spatialQueryService: SpatialQueryService | null = null
  private readonly records = new Map<string, CharacterRecord>()
  private readonly viewBindings = new Map<string, CharacterViewBinding>()
  private nextId = 0
  private disposed = false

  constructor(private readonly onVisualChange: (reason: string) => void = () => {}) {}

  setSpatialQueryService(service: SpatialQueryService): void {
    this.spatialQueryService = service
  }

  resolveStablePlacement(options: CharacterStablePlacementOptions): CharacterStablePlacementResult {
    this.assertUsable()
    this.assertSpatialQueryService()
    const spatialQuery = { ...(options.spatialQuery ?? {}) }
    const motor = new CapsuleCharacterMotor(
      { ...this.createMotorQueries(spatialQuery), ensureCoverage: options.ensureCollisionCoverage },
      options.motorOptions
    )
    return motor.resolveStablePlacement(options)
  }

  create(options: CharacterCreateOptions = {}): CharacterHandle {
    this.assertUsable()
    this.assertSpatialQueryService()

    const id = options.id ?? `character-${++this.nextId}`
    if (this.records.has(id)) {
      throw new Error(`CharacterService: character "${id}" already exists`)
    }

    const command = mergeCharacterCommand(DEFAULT_COMMAND, options.command)
    const spatialQuery = { ...(options.spatialQuery ?? {}) }
    const motor = new CapsuleCharacterMotor(
      { ...this.createMotorQueries(spatialQuery), ensureCoverage: options.ensureCollisionCoverage },
      options.motorOptions
    )

    if (options.floorPoint) {
      motor.setFloorPoint(options.floorPoint)
    } else if (options.capsuleStart) {
      motor.setCapsuleStart(options.capsuleStart)
    }
    motor.setOrientation(command.bodyYaw, command.aimYaw, command.aimPitch)

    const handle = new CharacterHandleImpl(id, this)
    const record: CharacterRecord = {
      id,
      handle,
      motor,
      command,
      snapshot: this.withId(id, motor.getSnapshot()),
      spatialQuery,
    }
    this.records.set(id, record)
    this.onVisualChange('character.created')
    return handle
  }

  get(id: string): CharacterHandle | undefined {
    this.assertUsable()
    return this.records.get(id)?.handle
  }

  list(): CharacterHandle[] {
    this.assertUsable()
    return Array.from(this.records.values(), (record) => record.handle)
  }

  remove(id: string): boolean {
    this.assertUsable()
    const removed = this.records.delete(id)
    if (removed) {
      this.clearBindingsForCharacter(id)
      this.onVisualChange('character.removed')
    }
    return removed
  }

  update(delta: number): void {
    this.assertUsable()
    for (const record of this.records.values()) {
      record.snapshot = this.withId(record.id, record.motor.update(record.command, delta))
      record.command = {
        ...record.command,
        move: { ...record.command.move },
        jump: false,
      }
    }
  }

  getSnapshot(id: string): CharacterSnapshot {
    return cloneCharacterSnapshot(this.requireRecord(id).snapshot)
  }

  getCommand(id: string): CharacterCommand {
    return cloneCharacterCommand(this.requireRecord(id).command)
  }

  setCommand(id: string, command: Partial<CharacterCommand>): CharacterSnapshot {
    const record = this.requireRecord(id)
    record.command = mergeCharacterCommand(record.command, command)
    record.motor.setOrientation(
      record.command.bodyYaw,
      record.command.aimYaw,
      record.command.aimPitch
    )
    record.snapshot = this.withId(record.id, record.motor.getSnapshot())
    this.onVisualChange('character.command')
    return cloneCharacterSnapshot(record.snapshot)
  }

  replaceCommand(id: string, command: CharacterCommand): CharacterSnapshot {
    const record = this.requireRecord(id)
    record.command = cloneCharacterCommand(command)
    record.motor.setOrientation(
      record.command.bodyYaw,
      record.command.aimYaw,
      record.command.aimPitch
    )
    record.snapshot = this.withId(record.id, record.motor.getSnapshot())
    this.onVisualChange('character.command')
    return cloneCharacterSnapshot(record.snapshot)
  }

  resetCommand(id: string): CharacterSnapshot {
    return this.replaceCommand(id, DEFAULT_COMMAND)
  }

  bindViewCharacter(
    viewId: string,
    characterId: string,
    options: CharacterViewBindingOptions = {}
  ): CharacterViewBinding {
    this.requireRecord(characterId)

    const binding: CharacterViewBinding = {
      viewId,
      characterId,
      firstPerson: normalizeFirstPersonViewOptions(options.firstPerson),
      thirdPerson: normalizeThirdPersonViewOptions(options.thirdPerson),
    }
    this.viewBindings.set(viewId, binding)
    this.onVisualChange('character.bindingChanged')
    return cloneCharacterViewBinding(binding)
  }

  unbindViewCharacter(viewId: string): boolean {
    this.assertUsable()
    const removed = this.viewBindings.delete(viewId)
    if (removed) {
      this.onVisualChange('character.bindingChanged')
    }
    return removed
  }

  getViewBinding(viewId: string): CharacterViewBinding | null {
    this.assertUsable()
    const binding = this.viewBindings.get(viewId)
    return binding ? cloneCharacterViewBinding(binding) : null
  }

  listViewBindings(): CharacterViewBinding[] {
    this.assertUsable()
    return Array.from(this.viewBindings.values(), cloneCharacterViewBinding)
  }

  setCapsuleStart(
    id: string,
    point: Parameters<CapsuleCharacterMotor['setCapsuleStart']>[0]
  ): CharacterSnapshot {
    const record = this.requireRecord(id)
    record.motor.setCapsuleStart(point)
    record.snapshot = this.withId(record.id, record.motor.getSnapshot())
    this.onVisualChange('character.pose')
    return cloneCharacterSnapshot(record.snapshot)
  }

  setFloorPoint(
    id: string,
    point: Parameters<CapsuleCharacterMotor['setFloorPoint']>[0]
  ): CharacterSnapshot {
    const record = this.requireRecord(id)
    record.motor.setFloorPoint(point)
    record.snapshot = this.withId(record.id, record.motor.getSnapshot())
    this.onVisualChange('character.pose')
    return cloneCharacterSnapshot(record.snapshot)
  }

  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.records.clear()
    this.viewBindings.clear()
    this.spatialQueryService = null
  }

  private withId(id: string, snapshot: CapsuleCharacterMotorSnapshot): CharacterSnapshot {
    return {
      id,
      ...snapshot,
    }
  }

  private createMotorQueries(
    spatialQuery: SpatialQueryBindingOptions
  ): CapsuleCharacterMotorQueries {
    return {
      overlapCapsule: (capsule: CapsuleCharacterMotorSnapshot['capsule']) =>
        this.spatialQueryService!.overlapCollisionCapsule({
          ...spatialQuery,
          capsule,
        }),
      resolveCapsule: (capsule, maxIterations, skinWidth) =>
        this.spatialQueryService!.resolveCollisionCapsule({
          ...spatialQuery,
          capsule,
          maxIterations,
          skinWidth,
        }),
      sweepCapsule: (capsule, motion, maxSteps) =>
        this.spatialQueryService!.sweepCollisionCapsule({
          ...spatialQuery,
          capsule,
          motion,
          maxSteps,
        }),
      resolveGround: (origin, down, maxDistance) =>
        this.spatialQueryService!.resolveCollisionGround({
          ...spatialQuery,
          origin,
          down,
          maxDistance,
        }),
    }
  }

  private requireRecord(id: string): CharacterRecord {
    this.assertUsable()
    const record = this.records.get(id)
    if (!record) {
      throw new Error(`CharacterService: character "${id}" was not found`)
    }
    return record
  }

  private assertUsable(): void {
    if (this.disposed) {
      throw new Error('CharacterService has been disposed')
    }
  }

  private assertSpatialQueryService(): void {
    if (!this.spatialQueryService) {
      throw new Error('CharacterService requires SpatialQueryService before creating characters')
    }
  }

  private clearBindingsForCharacter(characterId: string): void {
    for (const [viewId, binding] of this.viewBindings) {
      if (binding.characterId === characterId) {
        this.viewBindings.delete(viewId)
      }
    }
  }
}

class CharacterHandleImpl implements CharacterHandle {
  constructor(
    readonly id: string,
    private readonly service: CharacterServiceImpl
  ) {}

  getSnapshot(): CharacterSnapshot {
    return this.service.getSnapshot(this.id)
  }

  getCommand(): CharacterCommand {
    return this.service.getCommand(this.id)
  }

  setCommand(command: Partial<CharacterCommand>): CharacterSnapshot {
    return this.service.setCommand(this.id, command)
  }

  replaceCommand(command: CharacterCommand): CharacterSnapshot {
    return this.service.replaceCommand(this.id, command)
  }

  resetCommand(): CharacterSnapshot {
    return this.service.resetCommand(this.id)
  }

  setCapsuleStart(
    point: Parameters<CharacterServiceImpl['setCapsuleStart']>[1]
  ): CharacterSnapshot {
    return this.service.setCapsuleStart(this.id, point)
  }

  setFloorPoint(point: Parameters<CharacterServiceImpl['setFloorPoint']>[1]): CharacterSnapshot {
    return this.service.setFloorPoint(this.id, point)
  }

  dispose(): void {
    this.service.remove(this.id)
  }
}

function mergeCharacterCommand(
  current: CharacterCommand,
  patch?: Partial<CharacterCommand>
): CharacterCommand {
  return {
    move: {
      forward: patch?.move?.forward ?? current.move.forward,
      right: patch?.move?.right ?? current.move.right,
    },
    run: patch?.run ?? current.run,
    jump: patch?.jump ?? current.jump,
    bodyYaw: patch?.bodyYaw ?? current.bodyYaw,
    aimYaw: patch?.aimYaw ?? current.aimYaw,
    aimPitch: patch?.aimPitch ?? current.aimPitch,
  }
}

function cloneCharacterCommand(command: CharacterCommand): CharacterCommand {
  return {
    ...command,
    move: { ...command.move },
  }
}

function cloneCharacterSnapshot(snapshot: CharacterSnapshot): CharacterSnapshot {
  return {
    ...snapshot,
    capsule: {
      start: snapshot.capsule.start.clone(),
      end: snapshot.capsule.end.clone(),
      radius: snapshot.capsule.radius,
    },
    floorPoint: snapshot.floorPoint.clone(),
    eyePosition: snapshot.eyePosition.clone(),
    velocity: snapshot.velocity.clone(),
    groundNormal: snapshot.groundNormal?.clone() ?? null,
  }
}

function normalizeFirstPersonViewOptions(
  options: Partial<CharacterFirstPersonViewOptions> | undefined
): CharacterFirstPersonViewOptions {
  const minPitch = options?.minPitch ?? DEFAULT_FIRST_PERSON_VIEW_OPTIONS.minPitch
  const maxPitch = options?.maxPitch ?? DEFAULT_FIRST_PERSON_VIEW_OPTIONS.maxPitch
  const normalizedMinPitch = Math.min(minPitch, maxPitch)
  const normalizedMaxPitch = Math.max(minPitch, maxPitch)

  return {
    lookInteraction: options?.lookInteraction ?? DEFAULT_FIRST_PERSON_VIEW_OPTIONS.lookInteraction,
    lookSensitivity: options?.lookSensitivity ?? DEFAULT_FIRST_PERSON_VIEW_OPTIONS.lookSensitivity,
    dragButton: options?.dragButton ?? DEFAULT_FIRST_PERSON_VIEW_OPTIONS.dragButton,
    pointerLockButton:
      options?.pointerLockButton ?? DEFAULT_FIRST_PERSON_VIEW_OPTIONS.pointerLockButton,
    minPitch: normalizedMinPitch,
    maxPitch: normalizedMaxPitch,
  }
}

function normalizeThirdPersonViewOptions(
  options: CharacterThirdPersonViewBindingOptions | undefined
): CharacterThirdPersonViewOptions {
  const minPitch = options?.minPitch ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.minPitch
  const maxPitch = options?.maxPitch ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.maxPitch
  const minDistance = options?.minDistance ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.minDistance
  const maxDistance = options?.maxDistance ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.maxDistance
  const normalizedMinPitch = Math.min(minPitch, maxPitch)
  const normalizedMaxPitch = Math.max(minPitch, maxPitch)
  const normalizedMinDistance = Math.max(0.1, Math.min(minDistance, maxDistance))
  const normalizedMaxDistance = Math.max(normalizedMinDistance, maxDistance)
  const followDistance = THREE.MathUtils.clamp(
    options?.followDistance ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.followDistance,
    normalizedMinDistance,
    normalizedMaxDistance
  )

  return {
    lookSensitivity: options?.lookSensitivity ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.lookSensitivity,
    dragButton: options?.dragButton ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.dragButton,
    followDistance,
    minDistance: normalizedMinDistance,
    maxDistance: normalizedMaxDistance,
    zoomSensitivity: options?.zoomSensitivity ?? DEFAULT_THIRD_PERSON_VIEW_OPTIONS.zoomSensitivity,
    minPitch: normalizedMinPitch,
    maxPitch: normalizedMaxPitch,
    cameraCollision: normalizeThirdPersonCameraCollisionOptions(options),
  }
}

function normalizeThirdPersonCameraCollisionOptions(
  options: CharacterThirdPersonViewBindingOptions | undefined
): CharacterThirdPersonViewOptions['cameraCollision'] {
  const defaults = DEFAULT_THIRD_PERSON_VIEW_OPTIONS.cameraCollision
  const collision = options?.cameraCollision
  return {
    ensureCollisionCoverage: collision?.ensureCollisionCoverage,
    enabled: collision?.enabled ?? defaults.enabled,
    radius: Math.max(0, collision?.radius ?? defaults.radius),
    skinWidth: Math.max(0, collision?.skinWidth ?? defaults.skinWidth),
    minDistance: Math.max(0.1, collision?.minDistance ?? defaults.minDistance),
    pullInSmoothing: Math.max(0.1, collision?.pullInSmoothing ?? defaults.pullInSmoothing),
    restoreSmoothing: Math.max(0.1, collision?.restoreSmoothing ?? defaults.restoreSmoothing),
  }
}

function cloneCharacterViewBinding(binding: CharacterViewBinding): CharacterViewBinding {
  return {
    viewId: binding.viewId,
    characterId: binding.characterId,
    firstPerson: {
      ...binding.firstPerson,
    },
    thirdPerson: {
      ...binding.thirdPerson,
      cameraCollision: {
        ...binding.thirdPerson.cameraCollision,
      },
    },
  }
}
